import { useState } from 'react';
import { alpha, Chip, Stack, styled, Typography, useTheme } from '@mui/material';
import { CheckOutlined, CloseOutlined } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';

// Utilities
import { useCompanyContribute } from 'src/zustand/useCompanyContribute';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import { StyledWhiteBox } from 'src/features/CompanyDetail/config';
import { useQueryListRelatedCompanies } from 'src/services/queries';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import { getColorPercentMatch, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import Table from 'src/components/Table';
import FlagCountry from 'src/components/common/FlagCountry';
import { formatNumberWithSuffix } from 'src/utils/helpers/number';
import PopoverAction from 'src/components/Table/PopoverAction';
import IconFeather from 'src/components/common/Icon';
import { AlertTriangle } from 'react-feather';
import { TypeContributeCompany } from 'src/features/CompanyDetail/Common/config';

export const StyledIconCheck = styled(CheckOutlined)(({ theme }) => ({
  borderRadius: '100%',
  fontSize: 22,
  padding: theme.spacing(0.5),
  background: alpha(theme.palette.success.dark, 0.07),
  color: theme.palette.success.main,
}));

export const StyledIconClose = styled(CloseOutlined)(({ theme }) => ({
  borderRadius: '100%',
  fontSize: 22,
  padding: theme.spacing(0.5),
  background: alpha(theme.palette.error.dark, 0.07),
  color: theme.palette.error.main,
}));

export default function RelatedCompanies() {
  const { id } = useParams();
  const theme = useTheme();
  const [params, setParams] = useState({
    limit: LIMIT_GET_LIST,
    offset: 0,
    sort_type: 'desc',
    sort_by: 'percent_match',
  });
  const { data, isFetching } = useQueryListRelatedCompanies(id || '', params);
  const { handleOpenContribute } = useCompanyContribute((state) => state);
  const listRelatedCompanies = getArrayDefault(data?.data);

  const configColumn: ConfigCell<IRelatedCompanies>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: 'Company Name',
      cell: (data: IRelatedCompanies) => (
        <RouterLinkNoUnderline to={AuthRouteKeys.CompanyDetail.replace(':id?', data.id)}>
          <Typography color={theme.palette.secondary.main} variant="body2">
            {renderTextOrDefault(data.company_name)}
          </Typography>
        </RouterLinkNoUnderline>
      ),
    },
    {
      disablePadding: false,
      id: 'jurisdiction',
      label: 'Jurisdiction',
      disableSort: true,
      cell: (data: IRelatedCompanies) => (
        <Stack alignItems={'center'} direction={'row'} spacing={1}>
          <FlagCountry code={data.country_code || ''} />
          <Typography color={theme.palette.text.secondary} variant="body2">
            {renderTextOrDefault(data.jurisdiction)}
          </Typography>
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'percent_match',
      label: 'Percent Match',
      cell: (data: IRelatedCompanies) => (
        <Chip
          label={`${data?.percent_match ?? 0}% Match`}
          color={getColorPercentMatch(data?.percent_match ?? 0)}
          size="small"
        />
      ),
    },
    {
      disablePadding: false,
      id: 'total_bol',
      label: 'Total B/L',
      cell: (data: IRelatedCompanies) => (
        <Typography color={theme.palette.text.secondary} variant="body2">
          {formatNumberWithSuffix(data.total_bol || 0)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'total_bol',
      label: 'Contact',
      disableSort: true,
      cell: (data: IRelatedCompanies) => (data.has_contacts ? <StyledIconCheck /> : <StyledIconClose />),
    },
    {
      id: 'action',
      disablePadding: false,
      label: 'Action',
      cell: (data: IRelatedCompanies) => {
        return (
          <PopoverAction
            id={`action-related-companies-${data.id}`}
            listAction={[
              {
                icon: (
                  <IconFeather
                    icon={<AlertTriangle />}
                    muiProps={{
                      color: 'primary',
                      fontSize: 'small',
                    }}
                  />
                ),
                text: 'Report Merge',
                action: () => {
                  handleOpenContribute(TypeContributeCompany.MergeCompanyProfile, {
                    mergeData: {
                      merge_company_profile_id: data.id,
                      merge_company_name: data.company_name || '',
                      merge_company_jurisdiction: data.jurisdiction || '',
                      merge_company_profile_type: getArrayDefault(data.official_tags),
                    },
                  });
                },
              },
            ]}
          />
        );
      },
    },
  ];

  if (isFetching) {
    return (
      <StyledWhiteBox>
        <LoadingSkeleton count={5} height={50} />
      </StyledWhiteBox>
    );
  }

  if (isEmpty(listRelatedCompanies)) {
    return <></>;
  }

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={1}>
      <Typography variant="h5" align="left">
        Related Company Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" align="left">
        These company profiles have similar names and may be related to the one you're viewing. We list them here for
        your reference. If you believe two profiles refer to the same company, please use the{' '}
        <Typography component="span" variant="body2" color="secondary.main">
          Contribute
        </Typography>{' '}
        feature to let us know. We will review and merge them if appropriate.
      </Typography>
      <Table
        maxRow={10}
        data={listRelatedCompanies}
        configColumn={configColumn}
        dataKey="id"
        totalRecords={data.total_records}
        pageSize={params.limit}
        page={params.offset}
        loading={isFetching}
        onChangePage={(e) => setParams({ ...params, ...{ offset: e } })}
        onChangePageSize={(e) => setParams({ ...params, ...{ offset: 0, limit: e } })}
        handleSort={(data) => {
          setParams({ ...params, ...{ sort_type: data.sort, sort_by: data.sortBy } });
        }}
      />
    </StyledWhiteBox>
  );
}
